import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { trackEvent } from 'analytics/analytics'
import { hotelRateImpressionEventWithContext } from 'analytics/snowplow/events'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import useLuxPlusPrimaryCashPrice from 'luxPlus/hooks/useLuxPlusPrimaryCashPrice'
import type { FlashHotelPackageDisplayedPricing } from '../getFlashHotelPackageDisplayedPricing'

interface UseFlashHotelPackageOptionImpressionOptions {
  offer: App.HotelOffer
  packageView: App.OfferPackageOptionView
  packageUniqueKey: string
  checkIn?: string
  checkOut?: string
  rooms: Array<OfferPageRoom>
}

/**
 * Sends a package-option impression after a Flash Hotel package card is sufficiently visible.
 * This stays at the Flash Hotel rate-option level instead of AccommodationSameRoomOptionTile:
 * the shared tile only owns layout and has no offer, package, occupancy or resolved-price data.
 * Keeping the observer here attaches it to the real tile while keeping product analytics out of
 * that generic UI primitive.
 */
export default function useFlashHotelPackageOptionImpression({
  offer,
  packageView,
  packageUniqueKey,
  checkIn,
  checkOut,
  rooms,
}: UseFlashHotelPackageOptionImpressionOptions) {
  const [displayedPricing, setDisplayedPricing] = useState<FlashHotelPackageDisplayedPricing>()
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const displayedPrice = useLuxPlusPrimaryCashPrice({
    price: displayedPricing?.nonMemberPrice ?? 0,
    memberPrice: displayedPricing?.memberPrice ?? 0,
    offerConfig: offer.luxPlus,
  })
  const reportDisplayedPricing = useCallback((pricing: FlashHotelPackageDisplayedPricing | undefined) => {
    setDisplayedPricing(current => (
      current?.nonMemberPrice === pricing?.nonMemberPrice &&
      current?.memberPrice === pricing?.memberPrice &&
      current?.value === pricing?.value ? current : pricing
    ))
  }, [])
  const [impressionRef, inView] = useInView({ threshold: 0.5 })
  const firedImpressions = useRef(new Set<string>())

  useLayoutEffect(() => {
    setDisplayedPricing(undefined)
  }, [checkIn, checkOut, packageUniqueKey])

  useEffect(() => {
    if (!inView || !displayedPricing) return

    const impressionKey = [packageUniqueKey, checkIn, checkOut].join(':')
    if (firedImpressions.current.has(impressionKey)) return

    trackEvent(hotelRateImpressionEventWithContext({
      offer,
      packageView,
      displayPrice: displayedPrice,
      checkIn,
      checkOut,
      occupancy: rooms,
      currency,
      displayMemberPrice: displayedPricing.memberPrice ?? displayedPricing.nonMemberPrice,
      displayNonMemberPrice: displayedPricing.nonMemberPrice,
      displayValue: displayedPricing.value,
    }))
    firedImpressions.current.add(impressionKey)
  }, [checkIn, checkOut, currency, displayedPrice, displayedPricing, inView, offer, packageUniqueKey, packageView, rooms])

  return { impressionRef, reportDisplayedPricing }
}
